// Weekly auto-update checker for the Talabya POS WebView APK.
//
// Compares the version returned by `window.TalabyaPrinter.health()`
// (injected by the Android WebView wrapper, see TalabyaJsBridge.kt) with
// the latest row from `/api/beta/latest-apk` (admin-managed via
// AdminBetaTestersModal). If a newer build is available, a dismissible
// banner slides down above the dashboard.
//
// Policy:
//   • Only runs inside the Talabya POS APK (window.TalabyaPrinter exists).
//     In Chrome / normal web users it's a no-op — nothing to update.
//   • Checks once per week per device (LRU'd in localStorage).
//   • Operator can "تذكيرني لاحقاً" → snooze for 24h.
//   • "تحديث الآن" → navigates to /api/beta/download/{apk_id} which triggers
//     the browser's native APK install dialog on Android.
import { useEffect, useState } from "react";
import api from "@/api/client";
import { Download, X, CheckCircle2 } from "lucide-react";

const SNOOZE_KEY = "talabya:apk-update-snooze";       // ms epoch — hide until this time
const LAST_CHECK_KEY = "talabya:apk-update-lastcheck"; // ms epoch of last network check
const CHECK_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;     // 1 week
const SNOOZE_MS = 24 * 60 * 60 * 1000;                 // 24h

// Compare two semver-ish version strings. Returns true if `a` is newer
// than `b`. Falls back to string comparison when the format is weird
// (e.g. date-based "2026.02.19" works bitwise too).
function isNewer(a, b) {
  if (!a || !b) return false;
  const pa = String(a).split(/[.\-]/).map((x) => parseInt(x, 10) || 0);
  const pb = String(b).split(/[.\-]/).map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const va = pa[i] || 0, vb = pb[i] || 0;
    if (va > vb) return true;
    if (va < vb) return false;
  }
  return false;
}

function readTalabyaVersion() {
  try {
    if (typeof window === "undefined") return null;
    if (!window.TalabyaPrinter?.health) return null;
    const raw = window.TalabyaPrinter.health();
    const j = JSON.parse(raw);
    return j?.version || null;
  } catch { return null; }
}

export default function ApkUpdateBanner() {
  const [latest, setLatest] = useState(null);     // { version, build, download_url, notes }
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const cur = readTalabyaVersion();
    if (!cur) return;  // Not running inside the native APK — nothing to check.
    setCurrent(cur);

    // Respect snooze — hide until the snooze timestamp expires.
    const snoozeUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozeUntil && Date.now() < snoozeUntil) return;

    // Respect weekly check cadence unless the user hasn't checked at all yet.
    const lastCheck = Number(localStorage.getItem(LAST_CHECK_KEY) || 0);
    const dueForCheck = !lastCheck || (Date.now() - lastCheck) > CHECK_INTERVAL_MS;
    if (!dueForCheck) return;

    (async () => {
      try {
        const { data } = await api.get("/beta/latest-apk");
        localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));
        if (data?.apk && isNewer(data.apk.version, cur)) {
          setLatest(data.apk);
          setVisible(true);
        }
      } catch { /* silent — try again next week */ }
    })();
  }, []);

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setVisible(false);
  };

  const dismissForever = () => {
    // "لا تظهر إشعارات هذا الإصدار" — park the snooze far enough that we
    // only ask again when the NEXT release ships.
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + CHECK_INTERVAL_MS * 2));
    setVisible(false);
  };

  const startDownload = () => {
    if (!latest?.download_url) return;
    const url = latest.download_url.startsWith("http")
      ? latest.download_url
      : `${process.env.REACT_APP_BACKEND_URL}${latest.download_url}`;
    // Opening in the same window triggers the Android download manager;
    // once complete the OS prompts to install.
    window.location.assign(url);
  };

  if (!visible || !latest) return null;

  return (
    <div
      className="mb-4 rounded-2xl border-2 border-emerald-300 bg-gradient-to-l from-emerald-50 to-emerald-100 p-3 flex items-center gap-3 talabya-shadow-soft animate-in slide-in-from-top"
      data-testid="apk-update-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
        <CheckCircle2 size={20}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-black text-sm text-emerald-900" style={{ fontFamily: "Cairo" }}>
          🎉 تحديث جديد متوفر للتطبيق — v{latest.version}
        </div>
        <div className="text-[11px] text-emerald-800/80 mt-0.5">
          لديك حالياً: v{current || "غير معروف"}
          {latest.notes ? ` · ${latest.notes.slice(0, 60)}${latest.notes.length > 60 ? "…" : ""}` : ""}
        </div>
      </div>
      <button
        onClick={startDownload}
        className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black transition"
        data-testid="apk-update-install-btn"
      >
        <Download size={13}/> تحديث الآن
      </button>
      <button
        onClick={snooze}
        className="shrink-0 px-2 py-1 rounded-lg bg-white text-emerald-700 text-[11px] font-black border border-emerald-300"
        data-testid="apk-update-snooze-btn"
      >
        لاحقاً
      </button>
      <button
        onClick={dismissForever}
        className="shrink-0 w-7 h-7 rounded-full hover:bg-emerald-200/60 text-emerald-700 flex items-center justify-center"
        title="تجاهل"
        data-testid="apk-update-dismiss-btn"
      >
        <X size={14}/>
      </button>
    </div>
  );
}
